/**
 * renderPreviewPng — rasterize a PREPARED color SVG into a preview JPG blob.
 *
 * VC-009 contract (name kept for compatibility):
 *   eng.renderPreviewPng(embeddedColorSvg) → JPG Blob | null
 *   (throws an Error on rasterization failure — fail-closed, WO §4 Q4,
 *   2026-08-07; the production caller catches and omits the optional
 *   email preview)
 * - requires the font-embedded, size-normalized color SVG string (output of
 *   prepareUploadPayload) as input;
 * - NEVER calls buildStickerSVG() — no second SVG generation; the preview
 *   derives from the same generatePayload() result as the other artifacts;
 * - loads the SVG through Blob URL → Image → Canvas;
 * - canvas 1200×1200; paints #ffffff before drawImage (JPEG has no alpha);
 * - returns image/jpeg at quality 0.85;
 * - returns null ONLY on the explicit guard (invalid input — caller
 *   programming error); throws an Error naming the failure on ANY
 *   rasterization failure, including canvas.toBlob() producing no blob
 *   (fail-closed, WO §4 Advisor ruling Q4, replaces the old
 *   catch (_) { return null; } silent fallback and the toBlob-null
 *   half-open path);
 * - always revokes its Blob URL.
 *
 * COMPATIBILITY DEBT (recorded in VC-009 change.md): the function and file
 * names say PNG, but the return type is a JPG Blob. Retained to keep the
 * storefront wiring surface small; a future API cleanup may rename.
 *
 * Loaded AFTER generatePayload.js and embedFonts.js.
 */
(function () {
  'use strict';

  var eng = window.StickerEngine = window.StickerEngine || {};
  var SIZE = 1200;
  var JPEG_QUALITY = 0.85;

  /**
   * OPEN-13 fix (2026-08-08, WO): WebKit rasterizes the filter regions of
   * an SVG image at the SVG's INTRINSIC size, then canvas drawImage
   * upscales. The prepared color SVG declares width/height = "2in"
   * (192px @ 96dpi — print semantics, set by prepareUploadPayload), so
   * Safari renders SVG filters at the intrinsic size and blows them up 6.25×
   * (blurry gold/silver). Chrome/Firefox re-rasterize at the destination
   * size. Fix: rewrite ONLY the root width/height to the raster size
   * (default 1200px) for the raster path. The caller's string is not
   * mutated; the production/color SVGs keep their print semantics.
   *
   * OPEN-13 v4 (2026-08-08, JJ 裁定): SVG 在 cart <img> 下任何尺寸都糊
   * (192/1200/200 全糊),栅格图则清晰 —— cart 改用 200px PNG(opts
   * { size: 200, format: 'png' },透明底无白边)。JPEG 默认路径不变
   * (白底, 邮件大图, 第一糊修复保持)。
   */
  function svgForRaster(svgStr, size) {
    return svgStr.replace(/<svg([^>]*)>/, function (m, attrs) {
      var out = attrs
        .replace(/\swidth\s*=\s*"[^"]*"/, ' width="' + size + '"')
        .replace(/\sheight\s*=\s*"[^"]*"/, ' height="' + size + '"');
      if (!/\swidth\s*=/.test(attrs)) out += ' width="' + size + '"';
      if (!/\sheight\s*=/.test(attrs)) out += ' height="' + size + '"';
      return '<svg' + out + '>';
    });
  }

  /**
   * Render the prepared color SVG to a raster Blob.
   * Throws on any rasterization failure (fail-closed, Q4 — including
   * toBlob() producing no blob); returns null only for the explicit
   * invalid-input guard.
   * @param {string} embeddedColorSvg — font-embedded, size-set color SVG
   * @param {object} [opts] — { size: raster edge in px (default 1200),
   *   format: 'jpeg' (default, white matte) | 'png' (transparent) }
   * @returns {Promise<Blob>} raster blob at size × size px
   * @throws {Error} rasterization failed — message names the failure
   *   (never a partial or silently-missing artifact)
   */
  eng.renderPreviewPng = async function (embeddedColorSvg, opts) {
    opts = opts || {};
    var size = opts.size || SIZE;
    var png = opts.format === 'png';
    try {
      if (typeof embeddedColorSvg !== 'string' || embeddedColorSvg.length === 0) {
        return null;
      }

      // OPEN-13 (2026-08-08): rasterize at intrinsic size — WebKit
      // would otherwise render SVG filters at the 2in (192px)
      // intrinsic size and upscale 6.25× (blurry). Caller's string is
      // untouched; only the raster-local copy is rewritten.
      var blob = new Blob([svgForRaster(embeddedColorSvg, size)], { type: 'image/svg+xml;charset=utf-8' });
      var url = URL.createObjectURL(blob);

      try {
        // Load SVG as an image (embedded @font-face applies in this path)
        var img = await loadImage(url);

        // Draw to canvas
        var canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        var ctx = canvas.getContext('2d');
        if (!png) {
          // JPEG: white matte (email large preview, 第一糊路径不变)
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, size, size);
        }
        // PNG: no fill — transparent matte (cart thumbnail, no white edges)
        ctx.drawImage(img, 0, 0, size, size);

        // Export as Blob (JPEG white-matte | PNG transparent)
        var outBlob = await new Promise(function (resolve) {
          canvas.toBlob(resolve, png ? 'image/png' : 'image/jpeg', png ? undefined : JPEG_QUALITY);
        });
        if (!outBlob) {
          // Fail-closed (Q4, 2026-08-07): toBlob yielding nothing is a
          // rasterization failure like any other — never a half-open
          // silent null.
          throw new Error('renderPreviewPng: canvas.toBlob() produced no ' + (png ? 'PNG' : 'JPG') + ' blob');
        }

        return outBlob;
      } finally {
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      // Fail-closed (WO §4, Advisor ruling Q4, 2026-08-07): never a
      // silently-missing or partial artifact. The production caller
      // (sticker-customizer liquid) catches and omits the optional email
      // preview; any other caller gets a named failure instead of null.
      var msg = 'renderPreviewPng: SVG→canvas rasterization failed' +
        (err && err.message ? ': ' + err.message : '');
      throw new Error(msg);
    }
  };

  // ── Promise-based Image loader ──────────────────────────
  function loadImage(src) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('SVG→Image failed to load')); };
      img.src = src;
    });
  }

})();
